/**
 * Endless - Security Authorisation auto-add
 *
 * What this does:
 *  - Watches the cart.
 *  - If any line item is a rental (line item property _is_rental === "true"),
 *    it adds the Security Authorisation deposit product to the cart, WITH the
 *    Downpay selling plan so Downpay places the hold.
 *  - If there are no rental lines left, it removes the deposit.
 *  - Only ever one deposit line.
 *  - After adding/removing the deposit, it reloads the page once so the change
 *    shows immediately. Quick refresh, always correct.
 *
 * This replaces what Product Rentals Pro used to do.
 */

(function () {
  "use strict";

  // ---- Config: real Endless values ----
  var DEPOSIT_VARIANT_ID = 47077011357941;   // Security Authorisation, Default Title
  var DEPOSIT_SELLING_PLAN_ID = 4417585397;  // Downpay "Not charged at checkout" plan
  var RENTAL_FLAG = "_is_rental";             // line item property that marks a rental
  var RENTAL_FLAG_VALUE = "true";

  // Guard so two cart events at once can't double-add.
  var working = false;

  // Guard so the reload can only happen once, never a loop.
  var reloadedOnce = false;

  function getCart() {
    return fetch("/cart.js", { headers: { Accept: "application/json" } })
      .then(function (r) { return r.json(); });
  }

  function isRentalLine(item) {
    return item.properties && item.properties[RENTAL_FLAG] === RENTAL_FLAG_VALUE;
  }

  function isDepositLine(item) {
    return item.variant_id === DEPOSIT_VARIANT_ID;
  }

  function addDeposit() {
    return fetch("/cart/add.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({
        items: [
          { id: DEPOSIT_VARIANT_ID, quantity: 1, selling_plan: DEPOSIT_SELLING_PLAN_ID }
        ]
      })
    }).then(function (r) { return r.json(); });
  }

  function removeDeposit(lineKey) {
    // Use the line item key so we only touch the deposit line.
    return fetch("/cart/change.js", {
      method: "POST",
      headers: { "Content-Type": "application/json", Accept: "application/json" },
      body: JSON.stringify({ id: lineKey, quantity: 0 })
    }).then(function (r) { return r.json(); });
  }

  function reconcile() {
    if (working) return Promise.resolve();
    working = true;

    return getCart()
      .then(function (cart) {
        var hasRental = (cart.items || []).some(isRentalLine);
        var depositLine = (cart.items || []).filter(isDepositLine)[0]; // undefined if none

        // Rental present, deposit missing -> add it.
        if (hasRental && !depositLine) {
          return addDeposit().then(function () { afterChange(); });
        }

        // No rental, deposit present -> remove it.
        if (!hasRental && depositLine) {
          return removeDeposit(depositLine.key).then(function () { afterChange(); });
        }

        // Already correct -> do nothing.
        return null;
      })
      .catch(function (err) {
        console.error("[security-deposit] reconcile failed:", err);
      })
      .then(function () {
        working = false;
      });
  }

  // After we add/remove the deposit, reload once so it shows immediately.
  function afterChange() {
    if (!reloadedOnce) {
      reloadedOnce = true;
      window.location.reload();
    }
  }

  // Watch every cart change by intercepting cart Ajax calls.
  function installCartHook() {
    var nativeFetch = window.fetch;
    window.fetch = function () {
      var args = arguments;
      var url = (args[0] && args[0].url) || args[0] || "";
      var p = nativeFetch.apply(this, args);
      try {
        if (
          typeof url === "string" &&
          /\/cart\/(add|change|update|clear)(\.js)?/.test(url) &&
          !working // don't react to our own calls
        ) {
          // React as soon as the theme's cart call finishes, no artificial delay.
          p.then(function () { reconcile(); });
        }
      } catch (e) {
        console.error("[security-deposit] hook error:", e);
      }
      return p;
    };
  }

  function init() {
    installCartHook();
    reconcile();
  }

  if (document.readyState === "loading") {
    document.addEventListener("DOMContentLoaded", init);
  } else {
    init();
  }
})();